import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'umi';
import { getProjectMessages, mapSentMailToMessage } from '@/services/api/communication';
import { processAPIResponseNow } from '@/utils/request';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { LeadActivityModel } from '@/typings/models/LeadActivity';
import { Message } from '@/components/Communication/types';
import { useLeadActivities } from '@/hooks/useLeadActivities';

export const useProjectDetails = (projectId: string): {
  leadData: LeadDetailsModel;
  activities: LeadActivityModel[];
  comments: LeadActivityModel[];
  messages: Message[];
  loading: boolean;
  refetch: () => void;
} => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState<Message[]>([]);

  const leadData = useSelector((state: DefaultRootState): LeadDetailsModel => {
    return (state?.leads?.details?.[projectId] || state?.leads?.leads?.[projectId] || {}) as LeadDetailsModel;
  });

  const loading = useSelector((state: DefaultRootState) => {
    return state?.loading?.effects?.['leads/fetchSingleLead'];
  });

  const { activities, comments, refresh: refreshActivities } = useLeadActivities({
    leadId: projectId,
  });

  const loadMessages = () => {
    if (!projectId) return;
    getProjectMessages(projectId, { page: 1, page_size: 5 }).then(APIResponse => {
      processAPIResponseNow(APIResponse, {
        onSuccess(response) {
          const mappedMessages = (response.data || []).map((m: any) =>
            mapSentMailToMessage(m, leadData?.customer?.email),
          );
          setMessages(mappedMessages);
        },
        onError() {},
      });
    });
  };

  useEffect(() => {
    if (projectId) loadMessages();
  }, [projectId]);

  // Every confirmed lead change (own PATCH response, newer websocket payload) also wrote
  // activity rows, but the timeline is its own endpoint — refetch it when `modified` moves.
  const lastConfirmed = useRef<{ projectId: string; modified?: string } | null>(null);
  useEffect(() => {
    const previous = lastConfirmed.current;
    lastConfirmed.current = { projectId, modified: leadData?.modified };
    if (!previous || previous.projectId !== projectId) return;
    if (previous.modified && leadData?.modified && previous.modified !== leadData.modified) {
      refreshActivities();
    }
  }, [projectId, leadData?.modified]);

  const refetch = () => {
    if (!projectId) return;
    dispatch({ type: 'leads/fetchSingleLead', payload: { leadId: projectId } });
    dispatch({ type: 'leads/fetchLinkedLeads', payload: { leadId: projectId } });
    refreshActivities();
    loadMessages();
  };

  return { leadData, activities, comments, messages, loading, refetch };
};
